import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Image, Navbar, Nav, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import '../css/header.css';

import { LanguageCode } from '../../common/language';
import DynamicHeightContext from '../../common/context/DynamicHeightContext';
import useIsScreenMdUp from '../../common/hooks/useIsScreenMdUp';
import useWindowSize from '../../common/hooks/useWindowSize';
import Icon from '../../common/components/Icon';
import CreateRecipeMenuItem from './CreateRecipeMenuItem';
import { AccountMenuMenuItem, AccountLoginMenuItem } from './MyAccountMenuItem';
import { getEnvAsBoolean, getResourcePath, getRoutePath } from '../../common/utility';
import { UserAccount } from '../../account/store/types';
import { Settings, ThemeMode } from '../../account/store/settings/types';
import LoginSettings from './LoginSettings';
import NavSearch from './NavSearch';
import NavLink from './NavLink';

export interface INavBarProps {
  account:  UserAccount | undefined;
  settings: Settings;

  locationPath: string;

  onChangeLanguage: (language: LanguageCode) => void;
  onChangeTheme: (theme: ThemeMode) => void;
  onLogoutClick: () => void;
}

const messages = defineMessages({
  page_navigation: {
    id: 'nav.aria_label',
    description: 'aria-label for the page navigation.',
    defaultMessage: 'Page navigation',
  },
  home: {
    id: 'nav.home',
    description: 'Home',
    defaultMessage: 'Home',
  },
  recipes: {
    id: 'nav.recipes',
    description: 'Navbar Recipes',
    defaultMessage: 'Browse',
  },
  randomRecipe: {
    id: 'nav.randomRecipe',
    description: 'Random Recipe',
    defaultMessage: 'Random',
  },
});

const NavBar: React.FC<INavBarProps> = ({
    account, settings, locationPath,
    onChangeLanguage,  onChangeTheme, onLogoutClick }: INavBarProps) => {
  const { formatMessage } = useIntl();

  const navbarRef = useRef<HTMLDivElement>(null);
  const dynamicHeightContext = useContext(DynamicHeightContext);

  const [width] = useWindowSize();
  useLayoutEffect(() => {
    dynamicHeightContext.setToolbarHeight(navbarRef.current?.clientHeight ?? 0);
  }, [dynamicHeightContext, width]);

  // componentWillUnmount
  useEffect(() => () => {
    dynamicHeightContext.setToolbarHeight(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isScreenMdUp = useIsScreenMdUp();
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);
  const handleExpandSearch = useCallback((expanded: boolean) => { setIsSearchExpanded(expanded); }, []);

  const isAuthenticated = account != null && account.id !== 0;
  const isPrivilegedUser = account != null && ['user', 'staff', 'admin'].includes(account.role);
  const isLoginRequired = getEnvAsBoolean('REACT_APP_REQUIRE_LOGIN');
  const isLoginPage = locationPath.endsWith('login');
  const isBrowserPage = locationPath.endsWith('browser');

  const myAccountBtn = isAuthenticated && (
    <AccountMenuMenuItem
        account  = {account}
        onLogoutClick = {onLogoutClick} />
  );
  const settingsBnt = (
    <LoginSettings
        settings = {settings}
        onChangeLanguage = {onChangeLanguage}
        onChangeTheme = {onChangeTheme} />
  );
  const loginBtn = (
    <AccountLoginMenuItem />
  );
  const navSearch = !isBrowserPage && (
    <NavSearch onExpandSearch={handleExpandSearch} />
  );

  return (
    <Navbar id='header-navbar' collapseOnSelect className={classNames('header', { authenticated : isAuthenticated })} expand='md' fixed='top' ref={navbarRef} aria-label={formatMessage(messages.page_navigation)}>
      <Container className={classNames({ 'search-expanded': isSearchExpanded })}>
        <Navbar.Toggle className='print-hidden'><Icon icon='list' variant='light' size='2x' /></Navbar.Toggle>
        <Navbar.Brand>
          <Link to={getRoutePath('/home')} title={formatMessage(messages.home)} aria-current={locationPath.endsWith('/home') ? 'page' : undefined}>
            <Image alt='Brand' src={getResourcePath('/images/chef.png')} width='30' height='30' className='d-inline-block align-top' />
          </Link>
        </Navbar.Brand>
        {!isScreenMdUp && (
          <div className='my-account-nav'>
            {isAuthenticated && (
              <>
                {navSearch}
                {myAccountBtn}
              </>
            )}
            {!isAuthenticated && (
              settingsBnt
            )}
            {!isAuthenticated && !isLoginPage && (
              loginBtn
            )}
          </div>
        )}
        <Navbar.Collapse>
          <Nav className={classNames('header-nav', { 'collapse-d-lg': isSearchExpanded })}>
            {(!isLoginRequired || isAuthenticated) && (!isScreenMdUp || locationPath.endsWith('/browser')) && (
              <NavLink to={getRoutePath('/browser')} active={locationPath.endsWith('/browser')} aria-current={locationPath.endsWith('/browser') ? 'page' : undefined}>
                <Icon icon='search' variant='light' className='d-md-inline-block d-none' />
                <span className='d-inline-block d-md-none'>{formatMessage(messages.recipes)}</span>
              </NavLink>
            )}
            {(!isLoginRequired || isAuthenticated) && <NavLink to={`${getRoutePath('/random')}?course__slug=Main`} active={locationPath.endsWith('/random')} aria-current={locationPath.endsWith('/random') ? 'page' : undefined} accessKey='r'>{formatMessage(messages.randomRecipe)}</NavLink>}
            {isAuthenticated && isPrivilegedUser && <CreateRecipeMenuItem />}
          </Nav>
          {isScreenMdUp && (
            <div className='header-nav my-account-nav'>
              {(!isLoginRequired || isAuthenticated) && navSearch}
              {isAuthenticated && (
                myAccountBtn
              )}
              {!isAuthenticated && (
                settingsBnt
              )}
              {!isAuthenticated && !isLoginPage && (
                loginBtn
              )}
            </div>
          )}
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavBar;
